/**
 * computeFlow.ts -- MER Algo Footprints v9.2.0
 *
 * Pure function: Trade[] -> FlowSignal.
 *
 * Phase 1 Session C: v1 detection rules. Conservative thresholds; tune in
 * later phases based on performance feedback (Phase 4 in the enhancement
 * plan: log every signal + outcome).
 *
 * Design notes:
 *
 *   - PURE FUNCTION. No I/O, no side effects, no time-of-day adjustments.
 *     The caller passes Trade[] and window bounds; we compute and return.
 *
 *   - Empty input -> no_signal with zero metrics, never throws.
 *
 *   - Block threshold (500 shares) is intentionally below the canonical
 *     10,000-share "institutional block" definition. Modern algorithmic
 *     execution slices large parent orders into smaller child orders;
 *     500-share TRF prints repeated near a level signal accumulation just
 *     as effectively as a single 10k block, and they're far more common.
 *
 *   - Confidence is bounded [0.0, 0.95]. We never return 1.0 because
 *     institutional flow is inferential, not deterministic.
 */

import type { Trade } from '../../marketData/schemas';
import type { FlowSignal, FlowBias, FlowMetrics } from '../schemas';

// ── Tunable thresholds (v1) ─────────────────────────────────────────────────

const BLOCK_THRESHOLD            = 500;        // shares; see Design notes
const OFF_EXCHANGE_STRONG_PCT    = 0.60;       // >60% off-exchange = strong
const OFF_EXCHANGE_NOISE_PCT     = 0.40;       // <40% = lit-driven, no signal
const VWAP_DELTA_STRONG_PCT      = 0.001;      // 0.1% above/below VWAP
const CONFIDENCE_CAP             = 0.95;

// ── Helpers ─────────────────────────────────────────────────────────────────

function emptySignal(symbol: string, fromMs: number, toMs: number): FlowSignal {
  return {
    symbol,
    bias:       'no_signal',
    confidence: 0,
    evidence:   ['No trades in window'],
    metrics: {
      totalVolume:       0,
      offExchangeVolume: 0,
      offExchangePct:    0,
      blockCount:        0,
      largestBlock:      0,
      vwap:              0,
      lastPrice:         0,
      vwapDelta:         0,
      tradeCount:        0,
    },
    windowMs: { from: fromMs, to: toMs },
  };
}

function aggregate(trades: Trade[]): FlowMetrics {
  let totalVolume       = 0;
  let offExchangeVolume = 0;
  let blockCount        = 0;
  let largestBlock      = 0;
  let priceVolumeSum    = 0;       // for VWAP
  let lastPrice         = 0;
  let lastTs            = 0;

  for (const t of trades) {
    totalVolume    += t.size;
    priceVolumeSum += t.price * t.size;
    if (t.size >= BLOCK_THRESHOLD) blockCount += 1;
    if (t.size > largestBlock)     largestBlock = t.size;
    if (t.isOffExchange)           offExchangeVolume += t.size;
    if (t.timestamp >= lastTs) {
      lastTs    = t.timestamp;
      lastPrice = t.price;
    }
  }

  const vwap          = totalVolume > 0 ? priceVolumeSum / totalVolume : 0;
  const offExchangePct = totalVolume > 0 ? offExchangeVolume / totalVolume : 0;
  const vwapDelta     = vwap > 0 ? (lastPrice - vwap) / vwap : 0;

  return {
    totalVolume,
    offExchangeVolume,
    offExchangePct,
    blockCount,
    largestBlock,
    vwap,
    lastPrice,
    vwapDelta,
    tradeCount: trades.length,
  };
}

function decideBias(m: FlowMetrics): FlowBias {
  // Very low off-exchange participation -> the move is lit-driven; we have
  // nothing institutional to say.
  if (m.offExchangePct < OFF_EXCHANGE_NOISE_PCT) return 'no_signal';

  // Need at least one meaningful block for either directional signal. This
  // filters out pure odd-lot/retail TRF noise that can push offExchangePct
  // up artificially in low-volume names.
  if (m.largestBlock < BLOCK_THRESHOLD) return 'neutral';

  // Strong off-exchange participation + price relative to VWAP gives direction
  if (m.offExchangePct >= OFF_EXCHANGE_STRONG_PCT) {
    if (m.vwapDelta > 0)  return 'bullish_accumulation';
    if (m.vwapDelta < 0)  return 'bearish_distribution';
    return 'neutral';
  }

  // Moderate off-exchange (40-60%): not strong enough for a directional call
  return 'neutral';
}

function computeConfidence(bias: FlowBias, m: FlowMetrics): number {
  if (bias === 'no_signal') return 0;
  if (bias === 'neutral')   return Math.min(m.offExchangePct * 0.5, 0.4);

  // Directional: base on offExchangePct, bump for confirming evidence
  let c = m.offExchangePct;
  if (m.blockCount >= 5)                          c += 0.10;
  if (m.largestBlock >= 1000)                     c += 0.10;
  if (Math.abs(m.vwapDelta) >= VWAP_DELTA_STRONG_PCT) c += 0.05;

  return Math.min(c, CONFIDENCE_CAP);
}

function buildEvidence(bias: FlowBias, m: FlowMetrics): string[] {
  if (bias === 'no_signal') {
    return [`Off-exchange volume only ${(m.offExchangePct * 100).toFixed(1)}% of total -- move is lit-driven`];
  }

  const ev: string[] = [];

  // Off-exchange share
  ev.push(`Off-exchange volume: ${(m.offExchangePct * 100).toFixed(1)}% of total (${m.offExchangeVolume.toLocaleString()} of ${m.totalVolume.toLocaleString()} shares)`);

  // Blocks
  if (m.blockCount > 0) {
    ev.push(`${m.blockCount} block print${m.blockCount === 1 ? '' : 's'} (>=${BLOCK_THRESHOLD} shares); largest ${m.largestBlock.toLocaleString()}`);
  }

  // VWAP context (directional bias only)
  if (bias === 'bullish_accumulation' || bias === 'bearish_distribution') {
    const direction = m.vwapDelta > 0 ? 'above' : 'below';
    ev.push(`Last price ${m.lastPrice.toFixed(2)} is ${(Math.abs(m.vwapDelta) * 100).toFixed(2)}% ${direction} VWAP ${m.vwap.toFixed(2)}`);
  }

  // Specific interpretation
  if (bias === 'bullish_accumulation') {
    ev.push('Pattern: heavy off-exchange buying with price holding above VWAP -- consistent with institutional accumulation');
  } else if (bias === 'bearish_distribution') {
    ev.push('Pattern: heavy off-exchange selling with price holding below VWAP -- consistent with institutional distribution');
  } else if (bias === 'neutral') {
    ev.push('Off-exchange activity elevated but no clear directional bias relative to VWAP');
  }

  return ev;
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Compute a FlowSignal from a window of trades.
 *
 * @param symbol  Uppercase ticker
 * @param trades  Normalized trades (filtered/cleaned upstream by polygonProvider)
 * @param fromMs  Window start (Unix ms)
 * @param toMs    Window end (Unix ms)
 */
export function computeFlow(
  symbol:  string,
  trades:  Trade[],
  fromMs:  number,
  toMs:    number,
  partial: boolean = false,
): FlowSignal {
  const sym = String(symbol || '').toUpperCase();

  if (!Array.isArray(trades) || trades.length === 0) {
    return emptySignal(sym, fromMs, toMs);
  }

  const metrics    = aggregate(trades);
  const bias       = decideBias(metrics);
  let   confidence = computeConfidence(bias, metrics);
  const evidence   = buildEvidence(bias, metrics);

  // Partial-data adjustment (Phase 1 hardening).
  //
  // When the upstream proxy hit its result cap (typically high-volume
  // symbols during active sessions), the trade window we computed over is
  // truncated -- we're seeing a biased subset, not the full picture.
  // Reduce confidence by 25% and emit explicit evidence so downstream
  // consumers (and the AI thesis) don't treat the signal as authoritative.
  if (partial && bias !== 'no_signal') {
    confidence = Math.max(0, confidence * 0.75);
    evidence.push('Partial data: upstream result cap hit; signal computed over a truncated window');
  }

  return {
    symbol:   sym,
    bias,
    confidence,
    evidence,
    metrics,
    windowMs: { from: fromMs, to: toMs },
  };
}
